import React, { useRef, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import * as echarts from 'echarts';
import { Modal, Button, Form, Bubble, Radio, InputAdornment, Input } from 'tea-component';
import { buriedBtnPoint } from '@/pages/home/utils/buriedPoint';
import { dbiBtnLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import { ECHART_DOWNLOAD_LOGO, ECHART_DOWNLOAD_LOGO_BLACK } from '@/pages/intelligence/pages/TopCharts/_config/echarts';
import { isPositiveNum, vaildNum } from '@/pages/intelligence/utils/commonUtils';
import type { IBaseProps } from '@/pages/intelligence/types/common';
import type { IEChartOption } from '@/pages/intelligence/types/echart';
import './index.scss';

interface IEChartDownloadModalProps extends IBaseProps {
  downloadLogConfigKey?: string;
  echartDownloadtitle?: string;
  echartDownloadOptions: IEChartOption;
  echartLegends?: string[];
  visible: boolean;
  onCancel: () => void;
  [key: string]: any;
}

export default function EChartDownloadModal(props: IEChartDownloadModalProps) {
  const {
    downloadLogConfigKey,
    echartDownloadtitle,
    echartDownloadOptions,
    echartLegends,
    selectedlegend,
    visible, // 弹框是否可见,当menuType 等于stats，弹框不可见
    onCancel,
  } = props;

  const [widthNum, setWidthNum] = useState(920);
  const [heightNum, setHeightNum] = useState(380);
  const [appliedType, setAppliedType] = useState('white');
  const [widthNumOut, setWidthNumOut] = useState(false);
  const [heightNumOut, setHeightNumOut] = useState(false);
  const [widthNumLess, setWidthNumLess] = useState(false);
  const [heightNumLess, setHeightNumLess] = useState(false);

  const [echartLoading, setEchartLoading] = useState(false);

  const mychart = useRef<any>(null);

  const handleAddGame = () => {
    // 创建虚拟DOM
    const VDOM = (
      <div
        id="echartDivId"
        style={{
          width: `${widthNum}px`,
          height: `${heightNum}px`,
        }}
      ></div>
    );

    if (echartDownloadOptions?.xAxis?.length > 0) {
      const echartOptions = echartDownloadOptions;
      for (const n in echartOptions.xAxis) {
        let obj = echartOptions.xAxis[n];
        obj = {
          ...obj,
          axisLine: {
            ...obj?.axisLine,
            lineStyle: {
              ...obj?.axisLine?.lineStyle,
              color: appliedType == 'white' ? '#E0E6F1' : '#fff',
              opacity: appliedType == 'white' ? 1 : 0.5,
            },
          },
          axisLabel: {
            ...obj?.axisLabel,
            color: appliedType == 'white' ? '#6E7079' : '#fff',
            opacity: appliedType == 'white' ? 1 : 0.6,
            fontSize: widthNum < 800 ? 10 : 12,
          },
        };
        echartOptions.xAxis[n] = obj;
      }

      for (const n in echartOptions.yAxis) {
        let obj = echartOptions.yAxis[n];
        obj = {
          ...obj,
          splitLine: {
            ...obj?.splitLine,
            lineStyle: {
              ...obj?.splitLine?.lineStyle,
              type: 'dashed',
              color: appliedType == 'white' ? '#E0E6F1' : '#fff',
              opacity: appliedType == 'white' ? 1 : 0.24,
            },
          },
          axisLabel: {
            ...obj?.axisLabel,
            color: appliedType == 'white' ? '#6E7079' : '#fff',
            opacity: appliedType == 'white' ? 1 : 0.6,
            fontSize: widthNum < 400 || heightNum < 300 ? 10 : 12,
          },
          splitNumber: heightNum < 300 ? 2 : 5,
        };
        echartOptions.yAxis[n] = obj;
      }

      const optObj = {
        backgroundColor: `${appliedType == 'white' ? '#fff' : '#000'}`,
        graphic: [
          {
            type: 'image', // 图形元素类型
            id: 'logo', // 更新或删除图形元素时指定更新哪个图形元素，如果不需要用可以忽略。
            right: 0.041 * widthNum, // 按设计稿比例，设置水印右边距
            top: (heightNum < 300 ? 0.16 : 0.12) * heightNum, // 根据父元素进行定位
            z: 0, // 层叠
            bounding: 'all', // 决定此图形元素在定位时，对自身的包围盒计算方式
            style: {
              image: appliedType == 'white' ? ECHART_DOWNLOAD_LOGO : ECHART_DOWNLOAD_LOGO_BLACK,
              width: 0.167 * heightNum * 4.48, // 按设计稿比例，设置水印宽、高
              height: 0.167 * heightNum, // 水印高度为选择高度的6分之1
              opacity: 0.15,
            },
          },
        ],
        animation: false,
        title: {
          ...echartOptions.title,
          textStyle: {
            ...echartOptions.title.textStyle,
            color: appliedType == 'white' ? '#000' : '#EBECF1',
            fontSize: heightNum < 300 ? 12 : 14,
          },
          top: '3%',
          left: '1%',
          text: echartOptions?.title?.text || echartDownloadtitle,
        },
        legend: {
          ...echartOptions.legend?.[0],
          textStyle: {
            color: appliedType == 'white' ? '#000' : '#EBECF1',
            fontSize: 12,
          },
          bottom: heightNum < 300 ? '0%' : '3%',
          itemWidth: heightNum < 300 ? 8 : 16,
          itemHeight: heightNum < 300 ? 8 : 16,
          itemGap: heightNum < 300 ? 20 : 18,
          top: heightNum < 300 ? 'bottom' : 'auto',
        },
        grid: {
          left: widthNum < 600 ? '5%' : '3%',
          right: heightNum < 300 ? '5%' : '4%',
          bottom: heightNum < 300 ? '18' : '10%',
          top: heightNum < 300 ? '18%' : '12%',
          containLabel: true,
        },
      };

      ReactDOM.render(VDOM, document.querySelector('#echartDownloadDivId'));

      mychart.current = echarts.init(document.getElementById('echartDivId'));

      // 根据用户选择的legend，修改下载的可视化数据
      if (selectedlegend) {
        const dataArr = echartOptions.legend.data || [];
        const seriesArr = echartOptions.series || [];
        if (dataArr.length > 0) {
          for (const n in selectedlegend) {
            for (const m in dataArr) {
              if (!selectedlegend[n] && dataArr[m] == n) {
                dataArr.splice(m, 1);
              }
              if (!selectedlegend[n] && seriesArr[m]?.name == n) {
                seriesArr.splice(m, 1);
              }
            }
          }
        }
        echartOptions.legend.data = dataArr;
        echartOptions.series = seriesArr;
      }
      if (Array.isArray(echartLegends)) {
        const selectedLegendsMap = echartOptions?.legend?.[0]?.selected;
        const selectedSeries: any[] = [];
        const selectedLegends: any[] = [];
        for (const echartLegend of echartLegends) {
          const isEChartLegendSelected = selectedLegendsMap?.[echartLegend] !== false;
          if (isEChartLegendSelected) {
            selectedLegends.push(echartLegend);
          }
        }
        optObj.legend.data = selectedLegends;
      }
      const optionsTemp = { ...echartOptions, ...optObj };
      // console.log('optionsTemp', optionsTemp);
      mychart.current.setOption(optionsTemp);

      setEchartLoading(true);
      setTimeout(() => {
        const link = document.createElement('a');
        link.download = echartOptions.title?.text || echartDownloadtitle || 'EchartImage';
        link.style.display = 'none';

        // let imgEchart = new Image();
        // imgEchart.src = mychart.current.getDataURL({
        //   type: 'png',
        // });

        // imgEchart.setAttribute("crossOrigin",'Anonymous');  //设置跨域

        link.href = mychart.current.getDataURL({
          type: 'png',
          // crossOrigin: 'Anonymous'
        }); // 导出图表图片，返回一个 base64 的 URL

        // link.href = imgEchart.src;
        document.body.appendChild(link);
        link.click();
        URL.revokeObjectURL(link.href); // 释放URL对象
        setEchartLoading(false);
        onCancel?.();
        handleResize();
      }, 1000);
    }
  };

  const handleResize = () => {
    try {
      mychart.current.resize();
    } catch (err) {}
  };

  const submitFormCancel = () => {
    onCancel?.();
  };

  const handleDownloadLog = (logConfigKey?: string) => {
    // @ts-ignore
    const logConfig = dbiBtnLogMes[logConfigKey];
    if (!logConfig) return;
    buriedBtnPoint(logConfig);
  };

  // 提交表单
  const submitForm = () => {
    handleAddGame();
    handleDownloadLog(downloadLogConfigKey);
  };

  // 设置submitResult值
  const changeSubmitResult = (val: string, type: string) => {
    const realVal = vaildNum(val);
    if (type == 'width') {
      setWidthNum(realVal);
      if (realVal * 1 > 2000) {
        setWidthNumOut(true);
        setWidthNumLess(false);
      } else if (realVal * 1 < 355) {
        setWidthNumOut(false);
        setWidthNumLess(true);
      } else {
        setWidthNumOut(false);
        setWidthNumLess(false);
      }
    } else {
      setHeightNum(realVal);
      if (realVal * 1 > 600) {
        setHeightNumOut(true);
        setHeightNumLess(false);
      } else if (realVal * 1 < 150) {
        setHeightNumOut(false);
        setHeightNumLess(true);
      } else {
        setHeightNumOut(false);
        setHeightNumLess(false);
      }
    }
  };

  const reset = () => {
    setWidthNum(920);
    setHeightNum(380);
    setAppliedType('white');
    setWidthNumOut(false);
    setHeightNumOut(false);
    setWidthNumLess(false);
    setHeightNumLess(false);
  };

  useEffect(() => {
    if (visible) return;
    mychart.current?.dispose();
    mychart.current = null;
    ReactDOM.unmountComponentAtNode(document.querySelector('#echartDownloadDivId') as Element);
    reset();
  }, [visible]);

  return (
    <>
      <Modal
        className={'detail-download-content width500'}
        visible={visible}
        caption={<span style={{ fontSize: 16, fontWeight: 500 }}>Chart Download Settings</span>}
        onClose={() => onCancel?.()}
      >
        <Modal.Body>
          <Form>
            <Form.Item
              label="Width"
              className={`downlaod-title-secondary echart-download-modal ${
                widthNumOut || widthNumLess ? 'widthNumOut' : ''
              }`}
            >
              <InputAdornment after="pixel">
                <Input
                  value={widthNum ? (widthNum === 'NaN' ? '' : widthNum) : isPositiveNum(widthNum) ? widthNum : ''}
                  onChange={(value, content) => changeSubmitResult(value, 'width')}
                />
              </InputAdornment>

              {(widthNumOut || widthNumLess) && (
                <Form.Control
                  showStatusIcon={false}
                  status={'error'}
                  message={widthNumLess ? 'The width cannot be less than 355' : 'The width cannot be greater than 2000'}
                  style={{ marginTop: 20 }}
                ></Form.Control>
              )}
            </Form.Item>
            <Form.Item
              label="Height"
              className={`downlaod-title-secondary echart-download-modal ${
                heightNumOut || heightNumLess ? 'heightNumOut' : ''
              }`}
            >
              <InputAdornment after="pixel">
                <Input
                  value={heightNum ? (heightNum === 'NaN' ? '' : heightNum) : isPositiveNum(heightNum) ? heightNum : ''}
                  onChange={(value, content) => changeSubmitResult(value, 'height')}
                />
              </InputAdornment>
              {(heightNumOut || heightNumLess) && (
                <Form.Control
                  showStatusIcon={false}
                  status={'error'}
                  message={
                    heightNumLess ? 'The Height cannot be less than 150' : 'The Height cannot be greater than 600'
                  }
                  style={{ marginTop: 20 }}
                ></Form.Control>
              )}
            </Form.Item>
          </Form>
          <Form className="file-format">
            <Form.Item label="Applied in">
              <Radio.Group
                className="Format"
                value={appliedType}
                onChange={(value) => {
                  setAppliedType(value);
                }}
              >
                <Radio name={'white'} key={1}>
                  {'White Background'}
                </Radio>
                <Radio name={'dark'} key={2}>
                  {'Dark Background'}
                </Radio>
              </Radio.Group>
            </Form.Item>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Bubble
            dark
            trigger="hover"
            placement="top"
            className="dowmload-tip-content new-tip"
            style={{ width: '100%', backgroundColor: '#000' }}
          >
            <Button
              disabled={widthNumOut || heightNumOut || widthNumLess || heightNumLess}
              type="primary"
              onClick={submitForm}
              loading={echartLoading}
            >
              Confirm
            </Button>
          </Bubble>
          <Button type="weak" onClick={submitFormCancel}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <div
        id="echartDownloadDivId"
        style={{
          display: 'none',
          // width: `${widthNum}+'px'`,
          // height: `${heightNum}+'px'`,
          // width: '1800px',
          // height: '800px'
        }}
      ></div>
    </>
  );
}
